import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Chip } from '@mui/material';
import QuantityStepper from './ui/QuantityStepper';
import { radius } from '../theme';

// Replaces FoodDialog + FoodDialogOwnAdded — the same "pick quantity/unit,
// see the calorie total, add" dialog. The unit-chip section only renders
// when `unit`/`setUnit` are provided, since own-added foods have no unit
// concept (they're tracked per-portion instead).
export default function QuantityFoodDialog({
  open,
  onClose,
  item,
  quantity,
  setQuantity,
  unit,
  setUnit,
  totalCalories,
  onAdd,
}) {
  if (!item) return null;

  const hasUnits = unit !== undefined && typeof setUnit === 'function';

  const units = hasUnits
    ? [
        { val: '100', label: `100 ${item.unit || 'g'}` },
        { val: item.unit || 'g', label: item.unit || 'g' },
        ...(item.kcal_serving ? [{ val: 'portion', label: 'Portion' }] : []),
      ]
    : [];

  const step = hasUnits ? (unit === 'g' || unit === 'ml' ? 10 : 1) : 1;
  const min = hasUnits ? step : 1;

  const calories =
    totalCalories !== undefined
      ? totalCalories
      : Math.round(item.kcal_complete * (Number(quantity) || 0));

  const handleAdd = () => (hasUnits ? onAdd() : onAdd(calories));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
          {item.product_name || '–'}
        </Typography>
        {!hasUnits && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {item.kcal_complete} kcal pro Portion
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ textAlign: 'center', py: 2.5, mb: 2.5, bgcolor: 'action.hover', borderRadius: radius.xl }}>
          <Typography variant="h2" fontWeight={800} color="primary.main" sx={{ lineHeight: 1, letterSpacing: '-1px' }}>
            {Math.round(calories)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {hasUnits ? 'kcal' : 'kcal gesamt'}
          </Typography>
        </Box>

        {hasUnits && (
          <>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Einheit
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
              {units.map((u) => (
                <Chip
                  key={u.val}
                  label={u.label}
                  onClick={() => setUnit(u.val)}
                  color={unit === u.val ? 'primary' : 'default'}
                  variant={unit === u.val ? 'filled' : 'outlined'}
                  sx={{ fontWeight: unit === u.val ? 700 : 400 }}
                />
              ))}
            </Box>
          </>
        )}

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {hasUnits ? 'Menge' : 'Portionen'}
        </Typography>
        <QuantityStepper value={quantity} onChange={setQuantity} min={min} step={step} />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ flex: 1 }}>
          Abbrechen
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={!quantity || Number(quantity) <= 0}
          sx={{ flex: 1, fontWeight: 700 }}
        >
          Hinzufügen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
